import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { CContainer, CFade } from '@coreui/react';

// routes config
import routes from '../routes';
import Spinner from './Spinner';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'src/store';
import NavigationBlocker from 'src/components/common/NavigationBlocker';
import BlockedModal from 'src/components/common/BlockedModal';
import type { History, Location } from 'history';
import { ServicePermission } from 'src/types/service';
import type { MutableRefObject } from 'react';
const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
);

const TheContent = () => {
  const history: History = useHistory();
  const location: Location = useLocation();
  const servicePermissions = useSelector((state: ApplicationState) => state.servicePermissions);
  const isDirty = useSelector((s: ApplicationState) => s.isDirty);
  const prevLocationRef = useRef(location.pathname);
  const ignoreNextHashChangeRef = useRef<boolean>(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const shouldBlockNavigation = (nextLocation: Location) => {
    if (isDirty && location.pathname.startsWith('/bid-plan-profit')) {
      return false;
    }
    const route = routes.find((route) => route.path === nextLocation.pathname);
    if (!route?.service_id) return false;
    const permission = servicePermissions.find(
      (permission: ServicePermission) => permission.service_id === route.service_id
    );
    return permission ? !permission.is_access_allowed : false;
  };

  const getPermissionMessage = (nextLocation: Location) => {
    const route = routes.find((route) => route.path === nextLocation.pathname);
    if (!route?.service_id) return '';

    const permission = servicePermissions.find(
      (permission: ServicePermission) => permission.service_id === route.service_id
    );
    return permission?.error_message || '';
  };

  const handleBlocked = (location: Location) => {
    setModalMessage(getPermissionMessage(location));
    setShowBlockedModal(true);
  };

  useEffect(() => {
    const onHashChange = () => {
      if (ignoreNextHashChangeRef.current) {
        ignoreNextHashChangeRef.current = false;
        return;
      }
      const hash = window.location.hash;
      const nextPath = hash.startsWith('#') ? hash.slice(1) : hash;
      const nextLocation = { pathname: nextPath } as Location;
      if (shouldBlockNavigation(nextLocation)) {
        const message = getPermissionMessage(nextLocation);
        if (message) setModalMessage(message);
        setShowBlockedModal(true);
        // history.replace(prevLocationRef.current);
        const currentPath = prevLocationRef.current;
        ignoreNextHashChangeRef.current = true;

        window.location.hash = `#${currentPath}`;
      } else {
        prevLocationRef.current = nextPath;
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [servicePermissions, history, isDirty]);

  useEffect(() => {
    const next = location.pathname;
    if (shouldBlockNavigation(location)) {
      const message = getPermissionMessage(location);
      if (message) setModalMessage(message);
      setShowBlockedModal(true);
      ignoreNextHashChangeRef.current = true;
      history.replace(prevLocationRef.current);
    } else {
      prevLocationRef.current = next;
    }
  }, [location.pathname, servicePermissions, history, isDirty]);
  useEffect(() => {
    if (!isDirty) {
      prevLocationRef.current = location.pathname;
    }
  }, [isDirty, location.pathname]);
  return (
    <>
      {showBlockedModal && (
        <BlockedModal
          showModal={showBlockedModal}
          message={modalMessage}
          onClose={() => setShowBlockedModal(false)}
        />
      )}
      <NavigationBlocker
        history={history}
        shouldBlockNavigation={shouldBlockNavigation}
        onBlocked={handleBlocked}
      />

      <main className="c-main">
        <CContainer fluid>
          <Suspense fallback={loading}>
            <Spinner />
            <Switch>
              {routes.map((route, idx) => {
                if (route.name === '入札計画収益') {
                  return (
                    <Route
                      key={idx}
                      path={route.path}
                      exact={route.exact}
                      // @ts-ignore
                      name={route.name}
                      service_id={route.service_id}
                      render={(props) => (
                        <CFade>
                          {
                            // @ts-ignore
                            <route.component
                              {...props}
                              ignoreNextHashChangeRef={
                                ignoreNextHashChangeRef as MutableRefObject<boolean>
                              }
                            />
                          }
                        </CFade>
                      )}
                    />
                  );
                } else {
                  return (
                    route.component && (
                      <Route
                        key={idx}
                        path={route.path}
                        exact={route.exact}
                        // @ts-ignore
                        name={route.name}
                        service_id={route.service_id}
                        render={(props: any) => (
                          <CFade>
                            <route.component {...props} />
                          </CFade>
                        )}
                      />
                    )
                  );
                }
              })}
              <Redirect to="/" />
            </Switch>
          </Suspense>
        </CContainer>
      </main>
    </>
  );
};

export default React.memo(TheContent);
